import * as THREE from 'three';

interface Wave {
  mesh: THREE.Mesh;
  age: number;
  duration: number;
  r0: number;
  r1: number;
  snap: boolean;
}

const SHAKE_DUR = 0.32;
const SHAKE_FAR = 26;

const _sphereGeo = new THREE.SphereGeometry(1, 32, 22);
const _discGeo = new THREE.PlaneGeometry(2, 2, 1, 1);

const SHELL_VERT = /* glsl */ `
varying vec3 vViewDir;
varying vec3 vNormal;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vViewDir = -mv.xyz;
  vNormal = normalize(normalMatrix * normal);
  gl_Position = projectionMatrix * mv;
}
`;

const SHELL_FRAG = /* glsl */ `
uniform float uProgress;
uniform vec3 uColor;
varying vec3 vViewDir;
varying vec3 vNormal;
void main() {
  float fres = 1.0 - abs(dot(normalize(vNormal), normalize(vViewDir)));
  float rim = smoothstep(0.18, 0.84, fres);
  float fade = pow(1.0 - uProgress, 1.12);
  float hot = pow(rim, 3.4) * fade;
  vec3 col = mix(uColor, vec3(1.0, 0.96, 0.78), hot);
  float alpha = rim * fade * 0.78;
  gl_FragColor = vec4(col * (0.65 + fade * 0.7), alpha);
}
`;

const RING_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const RING_FRAG = /* glsl */ `
uniform float uProgress;
uniform vec3 uColor;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float d = length(p);
  float w = 0.055 + uProgress * 0.07;
  float band = smoothstep(0.78 - w, 0.86, d) * (1.0 - smoothstep(0.9, 0.9 + w, d));
  float disc = 1.0 - smoothstep(0.97, 1.0, d);
  float fade = pow(1.0 - uProgress, 1.05);
  float alpha = band * disc * fade * 0.95;
  vec3 col = mix(uColor, vec3(1.0, 0.94, 0.72), 1.0 - uProgress);
  gl_FragColor = vec4(col * (1.05 + fade * 0.55), alpha);
}
`;

function makeMat(vert: string, frag: string, color: THREE.Color): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uProgress: { value: 0 },
      uColor: { value: color.clone() },
    },
    vertexShader: vert,
    fragmentShader: frag,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    fog: false,
    toneMapped: false,
  });
}

function blastFalloff(dist: number, radius: number): number {
  const near = Math.max(1.15, radius * 0.3);
  if (dist <= near) return 1;
  if (dist >= SHAKE_FAR) return 0;
  return 1 - (dist - near) / (SHAKE_FAR - near);
}

/** Expanding fresnel shell + ground ring, plus a tiny world rumble. */
export class ExplosionFx {
  private readonly group = new THREE.Group();
  private readonly live: Wave[] = [];
  private shakeT = 0;
  private shakeAmp = 0;
  private shakePhase = 0;

  constructor(
    scene: THREE.Scene,
    private readonly worldRoot: THREE.Object3D,
  ) {
    this.group.userData.ignoreHit = true;
    this.group.userData.noRoomCull = true;
    scene.add(this.group);
  }

  burst(origin: THREE.Vector3, radius: number, eye: THREE.Vector3, weaponId?: string): void {
    const rocket = weaponId === 'rpg';
    const shell = new THREE.Color(rocket ? 0xff7a3a : 0xffd56a);
    const ring = new THREE.Color(rocket ? 0xffaa55 : 0xffe8a0);
    const r = Math.max(2.4, radius);

    const snap = !rocket;
    this.spawnWave(origin, makeMat(SHELL_VERT, SHELL_FRAG, shell), 0.16, r * 2.55, snap ? 0.16 : 0.46, snap);
    this.spawnRing(origin, makeMat(RING_VERT, RING_FRAG, ring), 0.22, r * 3.15, snap ? 0.14 : 0.4, snap);
    this.spawnRing(origin, makeMat(RING_VERT, RING_FRAG, new THREE.Color(0xfff2c8)), 0.35, r * 2.2, snap ? 0.1 : 0.28, snap);

    const fall = blastFalloff(eye.distanceTo(origin), r);
    if (fall < 0.04) return;
    const amp = (0.011 + Math.min(r, 7) * 0.00105) * fall;
    this.shakeAmp = Math.max(this.shakeAmp, amp);
    this.shakeT = Math.max(this.shakeT, SHAKE_DUR);
  }

  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const w = this.live[i]!;
      w.age += dt;
      const u = Math.min(1, w.age / w.duration);
      const ease = w.snap ? u : 1 - (1 - u) * (1 - u);
      const r = w.r0 + (w.r1 - w.r0) * ease;
      w.mesh.scale.setScalar(r);
      const mat = w.mesh.material as THREE.ShaderMaterial;
      mat.uniforms.uProgress!.value = u;
      if (w.age >= w.duration) {
        this.group.remove(w.mesh);
        mat.dispose();
        this.live.splice(i, 1);
      }
    }

    if (this.shakeT <= 0) {
      if (this.shakeAmp > 0) {
        this.shakeAmp = 0;
        this.worldRoot.position.set(0, 0, 0);
      }
      return;
    }
    this.shakeT = Math.max(0, this.shakeT - dt);
    this.shakePhase += dt;
    const u = this.shakeT / SHAKE_DUR;
    const a = this.shakeAmp * u * u;
    const t = this.shakePhase;
    this.worldRoot.position.set(
      Math.sin(t * 64) * a,
      Math.sin(t * 79 + 1.1) * a * 0.42,
      Math.cos(t * 53) * a,
    );
    if (this.shakeT <= 0) {
      this.shakeAmp = 0;
      this.worldRoot.position.set(0, 0, 0);
    }
  }

  private spawnWave(
    origin: THREE.Vector3,
    mat: THREE.ShaderMaterial,
    r0: number,
    r1: number,
    duration: number,
    snap = false,
  ): void {
    const mesh = new THREE.Mesh(_sphereGeo, mat);
    mesh.position.copy(origin);
    mesh.scale.setScalar(r0);
    mesh.frustumCulled = false;
    mesh.userData.ignoreHit = true;
    this.group.add(mesh);
    this.live.push({ mesh, age: 0, duration, r0, r1, snap });
  }

  private spawnRing(
    origin: THREE.Vector3,
    mat: THREE.ShaderMaterial,
    r0: number,
    r1: number,
    duration: number,
    snap = false,
  ): void {
    const mesh = new THREE.Mesh(_discGeo, mat);
    mesh.position.set(origin.x, origin.y + 0.03, origin.z);
    mesh.rotation.x = -Math.PI / 2;
    mesh.scale.setScalar(r0);
    mesh.frustumCulled = false;
    mesh.userData.ignoreHit = true;
    this.group.add(mesh);
    this.live.push({ mesh, age: 0, duration, r0, r1, snap });
  }
}
